<template>
    <div class="mx-auto w-full max-w-sm lg:w-96">
        <div>
            <router-link to="/login"><img class="h-10 w-auto" src="@/assets/logo.svg" alt="Gallera"></router-link>
            <h2 class="mt-8 text-2xl/9 font-bold tracking-tight text-gray-900">{{ $t('auth_reset.title') }}</h2>
            <p class="mt-2 text-sm/6 text-gray-500">{{ $t('auth_reset.description') }}</p>
        </div>

        <div class="rounded-md bg-green-50 mt-4 p-4" v-if="isSuccess">
            <div class="flex">
                <div class="shrink-0">
                    <svg class="size-5 text-green-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clip-rule="evenodd" />
                    </svg>
                </div>
                <div class="ml-3">
                    <p class="text-sm font-medium text-green-800">
                        {{ $t('auth_reset.success') }}
                    </p>
                </div>
            </div>
        </div>

        <div class="mt-5 text-center text-sm/6" v-if="isSuccess">
            <router-link to="/login" class="font-semibold text-royal-blue-500 hover:text-royal-blue-600">
                {{ $t('auth_reset.login_with_new') }}
            </router-link>
        </div>

        <div class="mt-10" v-if="!isSuccess">
            <div>
                <form @submit.prevent="store" class="space-y-6">
                    <div>
                        <label for="email" class="block text-sm/6 font-medium text-gray-900">{{ $t('auth_reset.email') }}</label>
                        <div class="mt-2 grid grid-cols-1">
                            <input
                                id="email"
                                type="email"
                                :class="{
                                    'block w-full rounded-md bg-gray-100 px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 sm:text-sm/6': true,
                                    'col-start-1 row-start-1 block w-full outline-red-400': hasError('email'),
                                }"
                                readonly
                                name="email"
                                v-model="form.email"
                                required
                                autocomplete="email"
                                autofocus>
                            <ExclamationCircleIcon v-if="hasError('email')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                        </div>
                        <p class="mt-2 text-sm text-red-600" id="email-error" v-if="hasError('email')">{{ getError('email') }}</p>
                    </div>

                    <div>
                        <label for="password" class="block text-sm/6 font-medium text-gray-900">{{ $t('auth_reset.password') }}</label>
                        <div class="mt-2 grid grid-cols-1">
                            <input
                                id="password"
                                type="password"
                                :class="{
                                    'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-royal-blue-600 sm:text-sm/6': true,
                                    'col-start-1 row-start-1 block w-full text-red-400 outline-red-400': hasError('password'),
                                }"
                                name="password"
                                v-model="form.password"
                                required
                                autocomplete="current-password">
                            <ExclamationCircleIcon v-if="hasError('password')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                        </div>
                        <p class="mt-2 text-sm text-red-600" id="password-error" v-if="hasError('password')">{{ getError('password') }}</p>
                    </div>

                    <div>
                        <label for="password_confirmation" class="block text-sm/6 font-medium text-gray-900">{{ $t('auth_reset.password_confirmation') }}</label>

                        <div class="mt-2 grid grid-cols-1">
                            <input
                                id="password_confirmation"
                                type="password"
                                :class="{
                                    'block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-royal-blue-600 sm:text-sm/6': true,
                                    'col-start-1 row-start-1 block w-full text-red-400 outline-red-400': hasError('password'),
                                }"
                                name="password_confirmation"
                                v-model="form.password_confirmation"
                                required
                                autocomplete="current-password">
                            <ExclamationCircleIcon v-if="hasError('password_confirmation')" class="pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end text-red-500 sm:size-4" aria-hidden="true" />
                        </div>
                        <p class="mt-2 text-sm text-red-600" id="password-error" v-if="hasError('password_confirmation')">{{ getError('password_confirmation') }}</p>
                    </div>

                    <div>
                        <button type="submit" class="flex w-full justify-center rounded-md bg-royal-blue-500 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-royal-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-royal-blue-500 cursor-pointer">{{ $t('auth_reset.update_button') }}</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import {mapActions} from "vuex";
import {validationMethods} from "@/_helpers/validation";

import {ResponseError} from "@/types";
import {ExclamationCircleIcon} from "@heroicons/vue/16/solid";

export default defineComponent({
    name: 'ResetPassword',
    components: {ExclamationCircleIcon},

    data() {
        return {
            form: {
                token: null as string | null,
                email: null as string | null,
                password: null,
                password_confirmation: null,
            },

            isSuccess: false,

            errors: {} as { [key: string]: string[] },
        };
    },

    watch: {
        'form.email': function() {
            delete this.errors['email'];
        },
        'form.password': function() {
            delete this.errors['password'];
        },
    },

    computed: {
        formDisabled: function(): boolean {
            return this.form.email === null || this.form.email === ''
                || this.form.password === null || this.form.password === ''
                || this.form.password_confirmation === null || this.form.password_confirmation === ''
                || this.form.password != this.form.password_confirmation
                || this.form.email.indexOf('@') === -1;
        },

        resetToken: function (): string {
            return this.$route.params.token as string;
        },

        resetEmail: function (): string {
            return this.$route.query.email as string;
        }
    },

    mounted() {
        this.form.token = this.resetToken;
        this.form.email = this.resetEmail;
    },

    methods: {
        ...mapActions({
            resetPassword: "user/resetPassword",

            errorAlert: "alerts/error"
        }),

        ...validationMethods(),

        store() {
            this.resetPassword({
                token: this.form.token,
                email: this.form.email,
                password: this.form.password
            })
                .then(() => {
                    this.isSuccess = true;
                })
                .catch(({type, data}: {type: ResponseError, data: any}) => {
                    if(type == ResponseError.Validation) {
                        this.errors = data.data;

                        // this.errorAlert({
                        //     message: "Перевірте вірність заповнення полів",
                        // });
                    } else {
                        this.errorAlert({
                            message: data.message,
                        });
                    }
                });
        },
    }
});
</script>

<style scoped>

</style>